<template>
    <div
        v-if="isError"
        class="box-body with-border clearfix"
    >
        <div class="row">
            <div class="col-xs-12">
                <p>
                    {{ $t('messages.offer.tag.noTag', { id: tagId }) }}
                </p>
            </div>
        </div>
    </div>

    <ValidationObserver
        v-else
        v-slot="{ invalid }"
        ref="observer"
        tag="div"
    >
        <div class="box-body">
            <div class="row">
                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <ValidationProvider
                        v-slot="{ errors }"
                        name="name"
                        rules="required"
                        slim
                    >
                        <div
                            class="form-group"
                            :class="{ 'has-error': errors.length }"
                        >
                            <label>{{ $t('name') }} <b class="text-danger">*</b></label>

                            <input
                                v-model="tag.name"
                                type="text"
                                class="form-control"
                                :placeholder="`${$t('enter')} ${$t('name')}`"
                                :disabled="isLoading"
                            />

                            <div
                                v-if="errors.length"
                                class="help-block"
                                v-text="errors[0]"
                            />
                        </div>
                    </ValidationProvider>
                </div>

                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <div class="form-group">
                        <label>{{ $t('active') }}</label>

                        <FormSwitcher
                            v-model="tag.active"
                            :disabled="isLoading"
                        />
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <div class="form-group">
                        <label>{{ $t('offer.offers') }}</label>

                        <FormSelect
                            v-model="tag.offers"
                            :options="offers"
                            :placeholder="`${$t('select')} ${$t('offer.offers')}`"
                            multiple
                            searchable
                            allowEmpty
                            trackBy="id"
                            label="name"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div class="box-footer">
            <div class="row">
                <div class="col-xs-12">
                    <button
                        type="button"
                        class="btn btn-sm btn-primary"
                        :disabled="isLoading || invalid"
                        @click="submitTag"
                    >
                        <i
                            v-if="isLoading"
                            class="fa fa-refresh fa-spin"
                        />
                        <span>{{ isEdit ? 'Update' : 'Create' }}</span>
                    </button>

                    <button
                        class="btn btn-sm btn-default"
                        :disabled="isLoading"
                        @click="
                            $router.push({
                                name: 'offers.tags.list',
                            })
                        "
                    >
                        {{ $t('cancel') }}
                    </button>
                </div>
            </div>
        </div>
    </ValidationObserver>
</template>

<script>
import EventBus from '@/event-bus';
import FormSwitcher from '@/components/form/FormSwitcher';

export default {
    components: {
        FormSwitcher,
    },
    props: {
        tagId: {
            type: Number,
            default: undefined,
        },
    },
    data() {
        return {
            isError: false,
            isLoading: false,
            tag: {
                name: '',
                active: true,
                offers: [],
            },
            offers: [],
        };
    },
    computed: {
        isEdit() {
            return !!this.tagId;
        },
    },
    async created() {
        await this.fetchOfferTag();
        this.$emit('loaded', this.tag);
    },

    methods: {
        async fetchOfferTag() {
            try {
                this.isLoading = true;

                const {
                    data: { tag, offers },
                } = await axios.get(`/offers/tags/data/${this.tagId || 0}`);

                if (!Array.isArray(tag)) {
                    this.tag = tag;
                }

                this.offers = offers;
            } catch (error) {
                this.isError = true;
            } finally {
                this.isLoading = false;
            }
        },

        async submitTag() {
            if (!(await this.$refs.observer.validate())) {
                return;
            }

            try {
                this.isLoading = true;

                const url = this.isEdit ? `/offers/tags/data/${this.tagId}` : '/offers/tags/data';
                const data = {
                    ...this.tag,
                    offers: this.tag.offers.map((offer) => offer.id),
                };

                const {
                    data: { action, tag },
                } = await axios({
                    method: this.isEdit ? 'PUT' : 'POST',
                    url,
                    data,
                });

                if (action === 'created') {
                    this.onCreated(tag.name);
                } else if (action === 'updated') {
                    this.onUpdated(tag.id, tag.name);
                }
            } catch (error) {
                const {
                    data: { errors, message },
                } = error.response;

                if (errors) {
                    this.$refs.observer.setErrors(errors);
                }

                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message,
                    errors,
                });
            } finally {
                this.isLoading = false;
            }
        },

        async onUpdated(id, name) {
            if (!id && !name) return;

            await this.$router.push({
                name: 'offers.tags.show',
                id,
            });

            EventBus.$emit('alertData', {
                class: 'alert-success',
                message: this.$t('messages.offer.tag.updated', { name }),
            });
        },

        async onCreated(name) {
            if (!name) return;

            await this.$router.push({
                name: 'offers.tags.list',
            });

            EventBus.$emit('alertData', {
                class: 'alert-success',
                message: this.$t('messages.offer.tag.created', { name }),
            });
        },
    },
};
</script>
